import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load all favorites for the current user
  const loadFavorites = useCallback(async () => {
    if (!user) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setFavorites(data || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Check if an item is in favorites
  const isFavorite = useCallback((itemId, itemType) => {
    return favorites.some(f => f.item_id === itemId && f.item_type === itemType);
  }, [favorites]);

  // Toggle favorite status
  const toggleFavorite = useCallback(async (itemId, itemType) => {
    if (!user) {
      toast.error('Accedi per salvare i preferiti');
      return false;
    }

    const isCurrentlyFavorite = isFavorite(itemId, itemType);

    try {
      if (isCurrentlyFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('item_id', itemId)
          .eq('item_type', itemType);

        if (error) throw error;
        
        setFavorites(prev => prev.filter(f => !(f.item_id === itemId && f.item_type === itemType)));
        toast.success('Rimosso dai preferiti');
        return false;
      } else {
        // Add to favorites
        const { data, error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            item_id: itemId,
            item_type: itemType
          })
          .select()
          .single();

        if (error) throw error;
        
        setFavorites(prev => [...prev, data]);
        toast.success('Aggiunto ai preferiti');
        return true;
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Errore nel salvare il preferito');
      return isCurrentlyFavorite;
    }
  }, [user, isFavorite]);

  // Remove a favorite
  const removeFavorite = useCallback(async (itemId, itemType) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('item_id', itemId)
        .eq('item_type', itemType);

      if (error) throw error;
      
      setFavorites(prev => prev.filter(f => !(f.item_id === itemId && f.item_type === itemType)));
      toast.success('Rimosso dai preferiti');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Errore nella rimozione');
    }
  }, [user]);

  // Get favorites by type
  const getFavoritesByType = useCallback((itemType) => {
    return favorites.filter(f => f.item_type === itemType);
  }, [favorites]);

  return {
    favorites,
    loading,
    isFavorite,
    toggleFavorite,
    removeFavorite,
    getFavoritesByType,
    refreshFavorites: loadFavorites
  };
}
